/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent } from 'react';

interface ICreditsProps {
    className?: string;
    creditString?: string;
    lines?: number;
}

type CreditCategory = {
    key: string;
    labelSingular: string;
    labelPlural: string;
    className: string;
    regex: RegExp;
};

const CREDIT_CATEGORIES: CreditCategory[] = [
    {
        key: 'anchor',
        labelSingular: 'Anchor: ',
        labelPlural: 'Anchors: ',
        className: 'anchors',
        regex: /(?:^|~)anchor:([^~]+)/i,
    },
    {
        key: 'host',
        labelSingular: 'Host: ',
        labelPlural: 'Hosts: ',
        className: 'hosts',
        regex: /(?:^|~)host:([^~]+)/i,
    },
    {
        key: 'actor',
        labelSingular: 'Actor: ',
        labelPlural: 'Actors: ',
        className: 'actors',
        regex: /(?:^|~)actor:([^~]+)/i,
    },
    {
        key: 'writer',
        labelSingular: 'Writer: ',
        labelPlural: 'Writers: ',
        className: 'writers',
        regex: /(?:^|~)writer:([^~]+)/i,
    },
    {
        key: 'director',
        labelSingular: 'Director: ',
        labelPlural: 'Directors: ',
        className: 'directors',
        regex: /(?:^|~)director:([^~]+)/i,
    },
    {
        key: 'producer',
        labelSingular: 'Producer: ',
        labelPlural: 'Producers: ',
        className: 'producers',
        regex: /(?:^|~)producer:([^~]+)/i,
    },
    {
        key: 'executiveProducer',
        labelSingular: 'Executive Producer: ',
        labelPlural: 'Executive Producers: ',
        className: 'executive-producers',
        regex: /(?:^|~)executiveProducer:([^~]+)/i,
    },
];

const extractCredits = (creditString: string, regex: RegExp): string[] => {
    const match = regex.exec(creditString);
    return match
        ? match[1]
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean)
        : [];
};

/**
 * Detail mode: Part 7
 * Display in order, as available. Only show label if metadata for that category is available.
 * No truncation or line limit.
 * If genre is "sports event", do not display.
 * Host: "Hosts: <hosts>":
 *      - "<hosts>" is a list of hosts as billed.
 *      - If there is only one host, display label as: "Host:"
 *      - Comma-separate names.
 * Actors: "Actors: <actors in billing order>":
 *      - "<actors in billing order>" is a list of actors as billed.
 *      - If there is only one actor, display label as: "Actor:"
 *      - Comma-separate names.
 * Writers: "Writers: <writers>":
 *      - "<writers>" is a list of writers as billed.
 *      - If there is only one writer, display label as: "Writer:"
 *      - Comma-separate names.
 * Directors: "Directors: <directors>":
 *      - "<directors>" is a list of directors as billed.
 *      - If there is only one director, display label as: "Director:"
 *      - Comma-separate names.
 * "Producers: <producers>":
 *      - "<producers>" is a list of producers as billed.
 *      - If there is only one producer, display label as: "Producer:"
 *      - Comma-separate names.
 * Executive producers: "Executive Producers: <executive producers>":
 *      - "<executive producers>" is a list of executive producers as billed.
 *      - If there is only one executive producer, display label as: "Executive Producer:"
 *      - Comma-separate names.
 */
const Credits: FunctionComponent<ICreditsProps> = ({
    className: aClassName = '',
    creditString = '',
    lines = 0,
}) => {
    if (!creditString) return null;
    const defaultClassNames = 'primary-text-style muted-color text-light';
    const lineHeightClass = `line-height-${lines}`;

    return (
        <div className={`uikit credits ${lineHeightClass} ${aClassName || defaultClassNames}`}>
            {CREDIT_CATEGORIES.map(({ key, labelSingular, labelPlural, className, regex }) => {
                const credits = extractCredits(creditString, regex);
                if (credits.length === 0) return null;
                const label = credits.length === 1 ? labelSingular : labelPlural;
                return (
                    <div className={className} key={key}>
                        {label}
                        {credits.map((name, idx) => (
                            <span key={name}>
                                {name}
                                {idx < credits.length - 1 ? ', ' : ''}
                            </span>
                        ))}
                    </div>
                );
            })}
        </div>
    );
};

export default Credits;
